// @flow

import type { Schedule } from './schedule.types.js';

export const parseMinutes = (stringDate: string): number => {
  const splitted = stringDate.split(':');
  return parseInt(splitted[0], 10) * 60 + parseInt(splitted[1], 10);
};

export function getMaxMinTime(schedules) {
  let min;
  let max;
  schedules.forEach(schedule => {
    if (!min || parseMinutes(schedule.startTime) < parseMinutes(min)) min = schedule.startTime;
    if (!max || parseMinutes(schedule.finishTime) > parseMinutes(max)) max = schedule.finishTime;
  });
  return { min, max };
}

// ---------- WORKING TIME LABELS ---------- //
export const generateLabels = (start: string, finish: string, timeFormat): number => {
  let startHours = Math.floor(parseMinutes(start) / 60);
  const finishHours = Math.ceil(parseMinutes(finish) / 60);

  const isFullDayFormat = timeFormat === 'H:mm';

  const labels = [];
  while (startHours < finishHours) {
    const isPm = startHours / 12 >= 1;
    labels.push(
      `${
        isFullDayFormat
          ? `${startHours.toString().padStart(2, '0')}:00`
          : isPm
          ? startHours === 12
            ? 12
            : startHours % 12
          : startHours === 0
          ? 12
          : startHours
      } ${isFullDayFormat ? '' : isPm ? ' PM' : ' AM'}`
    );
    startHours++;
  }

  return labels;
};

type ScheduleDateItem = {
  start: number,
  finish: number
};

// ---------- MERGE TIME INTERVALS ---------- //
export function concatDayScheduleItems(
  prev: ScheduleDateItem,
  start: number,
  finish: number
): ScheduleDateItem[] {
  return prev.reduce((acc, item) => {
    const nextItem = { ...item };

    // ---------- NON INTERSECTED ---------- //
    if (start < item.start && finish < item.start) {
      return [...acc, { start, finish }, nextItem];
    }

    if (start > item.finish) {
      return [...acc, nextItem, { start, finish }];
    }

    // ---------- INTERSECTED ---------- //
    if (start < item.start && finish > item.start) {
      nextItem.start = start;
    }

    if (finish > item.finish && start < item.finish) {
      nextItem.finish = finish;
    }

    return [...acc, nextItem];
  }, []);
}

export function getActualDaySchedule(
  current: Schedule,
  prev: ScheduleDateItem
): ScheduleDateItem[] {
  const start = parseMinutes(current.startTime);
  const finish = parseMinutes(current.finishTime);

  const interval = finish - start;

  if (interval) {
    if (prev) {
      return concatDayScheduleItems(prev, start, finish);
    }
    return [{ start, finish }];
  }
  return [];
}

type ScheduleMarkingItem = {
  offset: number,
  height: number
};

// ---------- MARKING ---------- //
export function getItemMarking(
  items: ScheduleDateItem[],
  globalStartTime: number
): ScheduleMarkingItem[] {
  if (!items.length) return [];

  let offsetStart = globalStartTime * 60;

  return items.map(item => {
    const currentOffset = item.start - offsetStart;
    offsetStart = item.finish;
    return {
      offset: currentOffset,
      height: item.finish - item.start
    };
  }, {});
}

export function getScheduleMarking(normSchedule: ScheduleDateItem[], globalStartTime: number) {
  return Object.keys(normSchedule).reduce((acc, key) => {
    const items = normSchedule[key];
    return {
      ...acc,
      [key]: getItemMarking(items, globalStartTime)
    };
  }, {});
}
